import { useState } from 'react';
import { Check, Layers, Monitor } from 'lucide-react';
import type { RepetitionType } from '@/features/campaigns/utils/startFreshCampaignCreate';
import { ConfirmAlertDialog } from './sections/ConfirmAlertDialog';

type ScheduleModePickerProps = {
  value: RepetitionType;
  onChange: (next: RepetitionType) => void;
  hasBookedSlots: boolean;
  onResetSlots: () => void;
  /** Hide when only one screen — both modes behave the same. */
  visible?: boolean;
};

const MODES: Array<{
  id: RepetitionType;
  label: string;
  summary: string;
  detail: string;
  icon: typeof Layers;
  accent: 'shared' | 'custom';
}> = [
  {
    id: 'random',
    label: 'Same for all',
    summary: 'Shared hours',
    detail:
      'Book dates and hours once. That schedule is applied to every selected screen — fastest when all displays should run the same plan.',
    icon: Layers,
    accent: 'shared',
  },
  {
    id: 'screen_wise',
    label: 'Per screen',
    summary: 'Custom hours',
    detail:
      'Configure each display separately. Use this when locations need different peak hours, plays, or coverage.',
    icon: Monitor,
    accent: 'custom',
  },
];

/**
 * Compact, high-contrast booking-mode chooser for Step 3.
 */
export function ScheduleModePicker({
  value,
  onChange,
  hasBookedSlots,
  onResetSlots,
  visible = true,
}: ScheduleModePickerProps) {
  const [pending, setPending] = useState<RepetitionType | null>(null);

  if (!visible) return null;

  const active = MODES.find((m) => m.id === value) ?? MODES[0];

  const requestChange = (next: RepetitionType) => {
    if (next === value) return;
    if (hasBookedSlots) {
      setPending(next);
      return;
    }
    onChange(next);
  };

  const confirmSwitch = () => {
    if (!pending) return;
    onResetSlots();
    onChange(pending);
    setPending(null);
  };

  return (
    <>
      <section className="mb-schedule-mode" aria-label="Schedule mode">
        <div className="mb-schedule-mode__bar">
          <div className="mb-schedule-mode__head">
            <p className="mb-schedule-mode__eyebrow">Booking style</p>
            <p className="mb-schedule-mode__lead">
              Choose how slots are applied across your selected screens
            </p>
          </div>

          <div
            className="mb-schedule-mode__grid"
            role="radiogroup"
            aria-label="How to book hours"
          >
            {MODES.map((mode) => {
              const selected = mode.id === value;
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-describedby="mb-schedule-mode-detail"
                  onClick={() => requestChange(mode.id)}
                  className={[
                    'mb-schedule-mode__card',
                    `mb-schedule-mode__card--${mode.accent}`,
                    selected ? 'is-selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="mb-schedule-mode__icon" aria-hidden>
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </span>
                  <span className="mb-schedule-mode__card-copy">
                    <span className="mb-schedule-mode__label">{mode.label}</span>
                    <span className="mb-schedule-mode__summary">
                      {mode.summary}
                    </span>
                  </span>
                  <span
                    className={[
                      'mb-schedule-mode__check',
                      selected ? 'is-on' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-hidden
                  >
                    {selected ? (
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>

          <p
            id="mb-schedule-mode-detail"
            className={`mb-schedule-mode__detail mb-schedule-mode__detail--${active.accent}`}
          >
            <strong>{active.label}.</strong> {active.detail}
          </p>
        </div>
      </section>

      <ConfirmAlertDialog
        open={pending != null}
        title="Switch schedule mode?"
        description="Your booked hours will be cleared so the new mode can start clean. Screens and campaign basics stay as they are."
        confirmLabel="Switch & clear hours"
        cancelLabel="Keep current"
        tone="warning"
        onCancel={() => setPending(null)}
        onConfirm={confirmSwitch}
      />
    </>
  );
}
